/**
 * EnhancedSpeechManager - Advanced speech manager with privacy controls and transcript management
 * 
 * Integrates phrase hints, transcript management, and enhanced UX controls
 */

import { SpeechManager } from './SpeechManager.js';
import { PhraseHints } from './PhraseHints.js';
import { TranscriptManager } from './TranscriptManager.js';

export class EnhancedSpeechManager extends SpeechManager {
    constructor(options = {}) {
        super(options);
        
        this.options = {
            // Enhanced features
            enablePhraseHints: options.enablePhraseHints !== false,
            enableTranscripts: options.enableTranscripts !== false,
            enablePushToTalk: options.enablePushToTalk !== false,
            
            // Privacy settings
            requireConfirmation: options.requireConfirmation !== false,
            autoDeleteTranscripts: options.autoDeleteTranscripts !== false,
            
            // Transcript settings
            maxTranscripts: options.maxTranscripts || 100,
            transcriptStorageKey: options.transcriptStorageKey || 'chef_transcripts',
            
            ...this.options,
            ...options
        };
        
        // Initialize enhanced features
        this.phraseHints = new PhraseHints();
        this.transcriptManager = new TranscriptManager({
            maxTranscripts: this.options.maxTranscripts,
            requireConfirmation: this.options.requireConfirmation,
            autoDeleteAfter: this.options.autoDeleteTranscripts ? 24 * 60 * 60 * 1000 : null,
            storageKey: this.options.transcriptStorageKey
        });
        
        this.isPushToTalkMode = false;
        this.currentRecipe = null;
        this.onTranscriptReceived = null;
        
        this.setupEnhancedCallbacks();

        // Simple event emitter for UI subscriptions
        this._listeners = new Map(); // event -> Set<handler>
    }

    /**
     * Setup enhanced callbacks
     */
    setupEnhancedCallbacks() {
        // Override the base result handler
        const originalOnResult = this.onResult;
        this.onResult = (transcript, isTriggered) => {
            // Call original handler
            if (originalOnResult) {
                originalOnResult(transcript, isTriggered);
            }
            
            // Handle transcript management
            if (this.options.enableTranscripts && isTriggered) {
                this.handleTranscriptReceived(transcript);
            }
        };
    }

    /**
     * Event Emitter API
     */
    on(event, handler) {
        if (!this._listeners.has(event)) {
            this._listeners.set(event, new Set());
        }
        this._listeners.get(event).add(handler);
    }

    off(event, handler) {
        if (this._listeners.has(event)) {
            this._listeners.get(event).delete(handler);
        }
    }

    emit(event, payload) {
        const set = this._listeners.get(event);
        if (set) {
            set.forEach(fn => {
                try { fn(payload); } catch (_) {}
            });
        }
    }

    /**
     * Override partial/final handling to emit events while preserving base behavior
     */
    handlePartialResult(result) {
        // Call base implementation
        try { super.handlePartialResult(result); } catch (_) {}
        // Emit event for UI without overriding pipeline
        this.emit('partial', result);
    }

    handleFinalResult(result) {
        try { super.handleFinalResult(result); } catch (_) {}
        this.emit('final', result);
    }

    /**
     * Handle transcript received
     * @param {string} transcript - Transcript text
     */
    handleTranscriptReceived(transcript) {
        const transcriptData = {
            text: transcript,
            timestamp: Date.now(),
            sessionId: this.options.sessionId,
            recipeId: this.currentRecipe?.id,
            recipeName: this.currentRecipe?.title,
            confidence: 0.9, // Default confidence
            engine: this.getCurrentEngineType(),
            metadata: {
                isPushToTalk: this.isPushToTalkMode,
                triggerPhrase: this.triggerPhrase
            }
        };
        
        const transcriptId = this.transcriptManager.addTranscript(transcriptData);
        
        if (this.onTranscriptReceived) {
            this.onTranscriptReceived({
                id: transcriptId,
                ...transcriptData
            });
        }
    }

    /**
     * Set recipe context for phrase hints
     * @param {Object} recipe - Recipe object
     */
    setRecipeContext(recipe) {
        this.currentRecipe = recipe;
        
        if (this.options.enablePhraseHints) {
            this.phraseHints.setRecipeContext(recipe);
            this.updateEnginePhraseHints();
        }
        
        // Update transcript manager context
        if (this.options.enableTranscripts) {
            this.transcriptManager.updateContext(
                this.options.sessionId,
                recipe?.id
            );
        }
    }

    /**
     * Update phrase hints for current engine
     */
    updateEnginePhraseHints() {
        if (!this.options.enablePhraseHints || !this.engine) return;
        
        const engineType = this.getCurrentEngineType();
        const hints = this.phraseHints.getFormattedHints(engineType);
        
        // Apply hints based on engine type
        switch (engineType) {
            case 'streaming':
                // For streaming STT, hints are sent with each session
                this.options.streamingOptions = {
                    ...this.options.streamingOptions,
                    phraseHints: hints.context,
                    boostedTerms: hints.boost
                };
                break;
                
            case 'browser':
                // For browser STT, we can't directly set grammars after creation
                // This would require recreating the engine
                console.log('Phrase hints available for browser STT:', hints.grammars);
                break;
                
            case 'vad':
                // Pass hints to VAD engine
                if (this.engine.updatePhraseHints) {
                    this.engine.updatePhraseHints(hints);
                }
                break;
        }
    }

    /**
     * Enable push-to-talk mode
     */
    enablePushToTalk() {
        this.isPushToTalkMode = true;
        this.stop(); // Stop continuous listening
    }

    /**
     * Disable push-to-talk mode
     */
    disablePushToTalk() {
        this.isPushToTalkMode = false;
    }

    /**
     * Start push-to-talk session
     * @returns {Promise<boolean>} Success status
     */
    async startPushToTalk() {
        if (!this.isPushToTalkMode) {
            this.enablePushToTalk();
        }
        
        return await this.start();
    }

    /**
     * Stop push-to-talk session
     * @returns {Promise<boolean>} Success status
     */
    async stopPushToTalk() {
        return await this.stop();
    }

    /**
     * Add custom phrase hints
     * @param {Array} hints - Custom hints to add
     */
    addCustomPhraseHints(hints) {
        if (this.options.enablePhraseHints) {
            this.phraseHints.addCustomHints(hints);
            this.updateEnginePhraseHints();
        }
    }

    /**
     * Clear custom phrase hints
     */
    clearCustomPhraseHints() {
        if (this.options.enablePhraseHints) {
            this.phraseHints.clearCustomHints();
            this.updateEnginePhraseHints();
        }
    }

    /**
     * Get phrase hints context summary
     * @returns {Object} Context summary
     */
    getPhraseHintsSummary() {
        return this.phraseHints.getContextSummary();
    }

    /**
     * Get transcript statistics
     * @returns {Object} Transcript statistics
     */
    getTranscriptStats() {
        return this.transcriptManager.getStats();
    }

    /**
     * Get recent transcripts
     * @param {number} limit - Number of transcripts to return
     * @returns {Array} Recent transcripts
     */
    getRecentTranscripts(limit = 10) {
        return this.transcriptManager.getRecentTranscripts(limit);
    }

    /**
     * Get pending transcripts
     * @returns {Array} Pending transcripts
     */
    getPendingTranscripts() {
        return this.transcriptManager.getPendingTranscripts();
    }

    /**
     * Confirm a transcript
     * @param {string} transcriptId - Transcript ID to confirm
     * @returns {boolean} Success status
     */
    confirmTranscript(transcriptId) {
        return this.transcriptManager.confirmTranscript(transcriptId);
    }

    /**
     * Reject a transcript
     * @param {string} transcriptId - Transcript ID to reject
     * @returns {boolean} Success status
     */
    rejectTranscript(transcriptId) {
        return this.transcriptManager.rejectTranscript(transcriptId);
    }

    /**
     * Search transcripts
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @returns {Array} Matching transcripts
     */
    searchTranscripts(query, options = {}) {
        return this.transcriptManager.searchTranscripts(query, options);
    }

    /**
     * Export transcripts
     * @param {Object} options - Export options
     * @returns {Object} Exported data
     */
    exportTranscripts(options = {}) {
        return this.transcriptManager.exportTranscripts(options);
    }

    /**
     * Clear transcripts
     * @param {Object} options - Clear options
     */
    clearTranscripts(options = {}) {
        this.transcriptManager.clearTranscripts(options);
    }

    /**
     * Set transcript received callback
     * @param {Function} callback - Callback function
     */
    setOnTranscriptReceived(callback) {
        this.onTranscriptReceived = callback;
    }

    /**
     * Get enhanced browser info
     * @returns {Object} Enhanced browser info
     */
    getBrowserInfo() {
        const baseInfo = super.getBrowserInfo();
        
        return {
            ...baseInfo,
            phraseHintsEnabled: this.options.enablePhraseHints,
            transcriptsEnabled: this.options.enableTranscripts,
            pushToTalkEnabled: this.options.enablePushToTalk,
            isPushToTalkMode: this.isPushToTalkMode,
            phraseHintsSummary: this.getPhraseHintsSummary(),
            transcriptStats: this.getTranscriptStats(),
            isEnhancedManager: true
        };
    }

    /**
     * Get enhanced engine statistics
     * @returns {Object} Enhanced engine statistics
     */
    getEngineStats() {
        const baseStats = super.getEngineStats();
        
        return {
            ...baseStats,
            phraseHints: this.getPhraseHintsSummary(),
            transcripts: this.getTranscriptStats(),
            pushToTalkMode: this.isPushToTalkMode,
            currentRecipe: this.currentRecipe?.title || null
        };
    }

    /**
     * Update session context
     * @param {string} sessionId - Session ID
     * @param {string} recipeId - Recipe ID
     */
    updateSessionContext(sessionId, recipeId) {
        this.options.sessionId = sessionId;
        
        if (this.options.enableTranscripts) {
            this.transcriptManager.updateContext(sessionId, recipeId);
        }
        
        // Update recipe context if we have the recipe
        if (this.currentRecipe && this.currentRecipe.id === recipeId) {
            this.setRecipeContext(this.currentRecipe);
        }
    }

    /**
     * Cleanup enhanced resources
     */
    destroy() {
        // Cleanup enhanced features
        if (this.transcriptManager) {
            this.transcriptManager.saveTranscripts();
        }
        
        // Clear callbacks
        this.onTranscriptReceived = null;
        this._listeners.clear();
        
        // Call parent cleanup
        super.destroy();
    }
}

